import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { useGoBack } from '../../hooks/useGoBack'
import { Gift, CheckCircle2, ExternalLink, Instagram, Link as LinkIcon, QrCode } from 'lucide-react'
import Header from '../../components/Header'
import { useI18n } from '../../i18n/useI18n'
import { allPrograms, useStampRally } from '../../store/useStampRally'
import { api, type StampBooth, type StampBoothLink } from '../../services/api'

/**
 * 出店店舗詳細 (#1126)。context.stores から解決（軽量マスタなので個別 fetch 不要）。
 * リンクは新規タブで外部遷移 — 会員IDなどは一切付けない (リンク先に情報を渡さない)。
 *
 * #1886 `?event=` があればイベント詳細から取り直す。**イベント詳細は context を
 * 持たない** (告知から来た人がそのまま読む公開ページなので、ジオフェンス判定を
 * 通っていない)。context 前提のままだと、そこからブースを開いた瞬間 TOP へ
 * 戻されていた。リハーサル中の未公開イベントも同じ理由で開けなかった。
 */
export default function StampBoothDetail() {
  const navigate = useNavigate()

  const { boothId } = useParams<{ boothId: string }>()
  const [params] = useSearchParams()
  const eventId = params.get('event')
  // #1361 戻るは履歴を1つ戻す（行き先を push すると履歴が積み上がる）。
  // 直リンクで履歴が無いときの行き先は、企画詳細から来たならそちら —
  // 一覧は context が要るので着地できず TOP へ弾かれる。
  // #1898 ブースはスタンプラリーの企画に付くので、`?event=` は企画の ID
  const goBack = useGoBack(eventId ? `/stamp-rally/program/${eventId}` : '/stamp-rally/stores')
  const { t } = useI18n()
  const { context } = useStampRally()

  // #1898 context はイベントごとにまとまっている。どの QR 企画のブースでも引けるように集める
  const fromContext = allPrograms(context)
    .flatMap((p) => (p.kind === 'qr' ? p.stores : []))
    .find((b) => b.id === boothId)
  const [fetched, setFetched] = useState<StampBooth | null>(null)
  const [notFound, setNotFound] = useState(false)
  const booth = fromContext ?? fetched

  // イベント詳細から来たとき (context なし) はイベントのブースから引き当てる
  useEffect(() => {
    if (fromContext || !eventId || !boothId) return
    let cancelled = false
    api
      .getStampEventInfo(eventId)
      .then((res) => {
        if (cancelled) return
        const hit = res.stores.find((b) => b.id === boothId)
        if (hit) setFetched(hit)
        else setNotFound(true)
      })
      .catch(() => { if (!cancelled) setNotFound(true) })
    return () => { cancelled = true }
  }, [fromContext, eventId, boothId])

  useEffect(() => {
    // `?event=` で開いた場合は取得の結果が出るまで待つ (context は元々無い)
    if (eventId) {
      if (notFound) navigate(`/stamp-rally/program/${eventId}`, { replace: true })
      return
    }
    if (!context) navigate('/stamp-rally', { replace: true })
    else if (context.available && !fromContext) navigate('/stamp-rally/stores', { replace: true })
  }, [context, fromContext, eventId, notFound, navigate])

  // #2111 出店者に見せる集客の実績に「説明を見られた回数」を足す。これまでは
  // QR を読んだ人しか数えられず、説明だけ読んで通り過ぎた人が見えなかった。
  // **開くたびに 1 回**。StrictMode の二重実行と、同じブースでの再描画は ref で弾く。
  const viewSentFor = useRef<string | null>(null)
  useEffect(() => {
    if (!booth || viewSentFor.current === booth.id) return
    viewSentFor.current = booth.id
    api.recordBoothView(booth.id)
  }, [booth])

  if (!booth) return null

  const linkLabel = (link: StampBoothLink) => {
    if (link.label) return link.label
    if (link.type === 'instagram') return t('stampRally.linkInstagram')
    if (link.type === 'hp') return t('stampRally.linkHp')
    return t('stampRally.linkOther')
  }

  return (
    <div className="min-h-screen bg-warm-white pb-10">
      <Header title={booth.name} onBack={goBack} />

      <div className="flex flex-col gap-4 px-4 mt-4">
        {/* #1886 **写真はそのままの比率で出す。** 高さを固定して object-cover にすると
            はみ出した部分が切り落とされ、ロゴや紹介写真を組んだ画像は絵が欠ける
            (実際 TOMOIKU BASE のロゴが切れていた)。
            縦長の写真で画面が埋まらないよう上限だけ設ける。 */}
        {booth.thumbnailUrl && (
          <img
            src={booth.thumbnailUrl}
            className="w-full max-h-[60vh] rounded-2xl object-contain"
            alt=""
          />
        )}

        <div className="bg-white rounded-2xl shadow-card p-4">
          <div className="flex items-center justify-between mb-1">
            <h1 className="font-display text-lg text-navy">{booth.name}</h1>
            {booth.stamped ? (
              <span className="flex items-center gap-1 text-xs font-body font-medium text-matcha shrink-0">
                <CheckCircle2 size={16} />
                {t('stampRally.stamped')}
              </span>
            ) : (
              <span className="text-xs font-body text-text-tertiary shrink-0">
                {t('stampRally.notStamped')}
              </span>
            )}
          </div>
          {booth.boothNo && (
            <p className="text-xs font-body text-text-tertiary mb-2">
              {t('stampRally.boothNo', { no: booth.boothNo })}
            </p>
          )}
          {booth.description && (
            <p className="text-sm font-body text-text-secondary whitespace-pre-wrap">
              {booth.description}
            </p>
          )}
          {/* #1886 ブースごとの特典。説明に混ぜると読み飛ばされるので独立して立てる。
              運営が入れた改行はそのまま活かす (特典が複数ある店がある)。 */}
          {booth.benefit && (
            <p className="mt-2 flex items-start gap-1.5 text-sm font-body text-gold">
              <Gift size={15} className="mt-0.5 shrink-0" />
              <span className="whitespace-pre-wrap">{booth.benefit}</span>
            </p>
          )}
        </div>

        {booth.links.length > 0 && (
          <div className="flex flex-col gap-2">
            {booth.links.map((link, i) => (
              <a
                key={i}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between bg-white rounded-xl shadow-card px-4 py-3.5 tap-card"
              >
                <span className="flex items-center gap-3 font-body text-sm text-text-primary">
                  {link.type === 'instagram' ? (
                    <Instagram size={18} className="text-pink-500" />
                  ) : (
                    <LinkIcon size={18} className="text-navy" />
                  )}
                  {linkLabel(link)}
                </span>
                <ExternalLink size={16} className="text-text-tertiary" />
              </a>
            ))}
          </div>
        )}

        {!booth.stamped && (
          <button
            onClick={() => navigate('/scan', { state: { purpose: 'stamp' } })}
            className="flex items-center justify-center gap-2 rounded-full bg-yokoso-red text-white py-3.5 text-sm font-body font-medium tap-effect"
          >
            <QrCode size={18} />
            {t('stampRally.openScanner')}
          </button>
        )}
      </div>
    </div>
  )
}
